import React, { useState, useEffect } from 'react';
import { FiEdit2, FiEye, FiSave, FiX, FiRefreshCw, FiCopy } from 'react-icons/fi';
import { getEmailTemplates, getEmailTemplate, updateEmailTemplate, previewEmailTemplate } from '../services/templates';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import toast from 'react-hot-toast';

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ subject: '', html_content: '', is_active: true });
  const [preview, setPreview] = useState(null);
  const [previewVariables, setPreviewVariables] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await getEmailTemplates();
      setTemplates(data.data || []);
    } catch (error) {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    setSelectedTemplate(template);
    setFormData({
      subject: template.subject,
      html_content: template.html_content,
      is_active: template.is_active,
    });
    setEditing(false);
    setPreview(null);
    
    // Initialize preview variables with examples
    const vars = {};
    template.variables?.forEach(v => {
      vars[v.name] = v.example || `[${v.name}]`;
    });
    setPreviewVariables(vars);
  };

  const handlePreview = async () => {
    try {
      const data = await previewEmailTemplate(selectedTemplate.code, previewVariables);
      setPreview(data.preview);
    } catch (error) {
      toast.error('Failed to preview template');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateEmailTemplate(selectedTemplate.code, formData);
      toast.success('Template updated successfully');
      fetchTemplates();
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleVariableClick = (varName) => {
    const textarea = document.querySelector('textarea[name="html_content"]');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = formData.html_content.substring(0, start) +
        `{{${varName}}}` +
        formData.html_content.substring(end);
      setFormData({ ...formData, html_content: newValue });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Email Templates</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Templates List */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Templates</h2>
          <div className="space-y-2">
            {templates.map((template) => (
              <button
                key={template._id}
                onClick={() => handleSelectTemplate(template)}
                className={`w-full text-left p-3 rounded-lg transition ${
                  selectedTemplate?._id === template._id
                    ? 'bg-primary-50 border-primary-500'
                    : 'hover:bg-gray-50 border-transparent'
                } border`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-800">{template.name}</p>
                    <p className="text-xs text-gray-500 font-mono">{template.code}</p>
                  </div>
                  {!template.is_active && (
                    <span className="text-xs text-red-500">Inactive</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Template Editor */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
          {selectedTemplate ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">
                    {selectedTemplate.name}
                  </h2>
                  <p className="text-xs text-gray-500 font-mono">{selectedTemplate.code}</p>
                </div>
                <div className="flex space-x-2">
                  {!editing ? (
                    <>
                      <button
                        onClick={() => setEditing(true)}
                        className="flex items-center px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      >
                        <FiEdit2 className="mr-1" /> Edit
                      </button>
                      <button
                        onClick={handlePreview}
                        className="flex items-center px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg"
                      >
                        <FiEye className="mr-1" /> Preview
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg"
                      >
                        {saving ? <FiRefreshCw className="animate-spin mr-1" /> : <FiSave className="mr-1" />}
                        Save
                      </button>
                      <button
                        onClick={() => setEditing(false)}
                        className="flex items-center px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                      >
                        <FiX className="mr-1" /> Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>

              {preview ? (
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium">Preview</h3>
                    <button
                      onClick={() => setPreview(null)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FiX />
                    </button>
                  </div>
                  <div 
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: preview }}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject
                    </label>
                    {editing ? (
                      <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    ) : (
                      <p className="text-gray-800 p-2 bg-gray-50 rounded-lg">{selectedTemplate.subject}</p>
                    )}
                  </div>

                  {/* HTML Content */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      HTML Content
                    </label>
                    {editing ? (
                      <textarea
                        name="html_content"
                        value={formData.html_content}
                        onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                        rows={12}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    ) : (
                      <div className="border rounded-lg p-4 max-h-96 overflow-auto bg-gray-50">
                        <div dangerouslySetInnerHTML={{ __html: selectedTemplate.html_content }} />
                      </div>
                    )}
                  </div>

                  {/* Variables */}
                  {selectedTemplate.variables?.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Available Variables
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {selectedTemplate.variables.map((v) => (
                          <button
                            key={v.name}
                            type="button"
                            onClick={() => handleVariableClick(v.name)}
                            className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-xs font-mono transition"
                            title={v.description}
                          >
                            {`{{${v.name}}}`}
                            <FiCopy className="inline ml-1 w-3 h-3" />
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Click on a variable to insert it at cursor position
                      </p>
                    </div>
                  )}

                  {/* Preview Variables (for preview mode) */}
                  {!editing && selectedTemplate.variables?.length > 0 && (
                    <div className="border-t pt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preview Variables
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedTemplate.variables.map((v) => (
                          <div key={v.name} className="flex items-center gap-2">
                            <span className="text-xs font-mono text-gray-500">{`{{${v.name}}}`}</span>
                            <input
                              type="text"
                              value={previewVariables[v.name] || ''}
                              onChange={(e) => setPreviewVariables({
                                ...previewVariables,
                                [v.name]: e.target.value
                              })}
                              placeholder={v.example}
                              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                            />
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={handlePreview}
                        className="mt-3 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600"
                      >
                        Update Preview
                      </button>
                    </div>
                  )}

                  {/* Status */}
                  {editing && (
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Active</span>
                      </label>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Select a template from the left to edit
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
